<script lang="ts" setup>
import ArrowSort from "~/assets/icons/arrows/arrowSort.svg"
import SearchClear from "~/assets/icons/close/closeSearch.svg"
import LoupeIconSmall from "~/assets/icons/loupe/16/loupe.svg"
import LoupeIconBig from "~/assets/icons/loupe/24/loupe.svg"
import AppCheckbox from "~/components/ui/form/formElements/AppCheckbox.vue"
import AppRadio from '~/components/ui/form/formElements/AppRadio.vue';
import AppButton from "~/components/ui/buttons/AppButton.vue";
import customSort from "~/composables/custom-sort";

import type { IFilter, IFiltersList } from '~/shared/interfaces/filtersList';

type TSelectedList = { [key: string]: IFilter };
type TSortingKeys = 'price' | 'alphabet';

const props = defineProps<IFiltersList>();
const emits = defineEmits(['init-sort', 'set-options-activity', 'apply-filters', 'clear-filters', 'close-dropdown', 'close-all-filter-modal']);

const searchStringValue = ref('');
const optionsList = ref<IFilter[]>([]);
const selectedFiltersList = ref<TSelectedList>({});

const sorting = ref<Record<TSortingKeys, boolean | null>>({
  price: null,
  alphabet: null,
})

watch(() => props.prioritySelectedOptions?.length, () => {
  if (props.prioritySelectedOptions) {
    selectedFiltersList.value = props.prioritySelectedOptions.reduce((acc: TSelectedList, option: IFilter) => {
      acc[option.id] = option;
      return acc;
    }, {})
  }
}, { immediate: true });

watch(() => props.isClearList, () => {
  clearFiltersList()
})

const getOptionsList = computed(() => {
  const { price, alphabet } = sorting.value;
  let optionsListResult: IFilter[] = optionsList.value;

  if (searchStringValue.value) {
    optionsListResult = optionsList.value.filter(option => String(option.name).includes(searchStringValue.value.toLowerCase()))
  }

  if (price != null || alphabet != null) {
    customSort({
      arr: optionsListResult,
      sortingType: (price != null && 'number') || (alphabet != null && 'alphabet') || 'number',
      field: (price != null && 'price') || (alphabet != null && 'name') || '',
      direction: (price || alphabet) ? 'asc' : 'desc',
    })
  }

  return optionsListResult;
})

optionsList.value = props.options;

function selectFilter(option: IFilter) {
  if (!props.isRadio) {
    if (selectedFiltersList.value[option.id]) {
      delete selectedFiltersList.value[option.id];
    } else {
      selectedFiltersList.value[option.id] = option;
    }

  } else {
    selectedFiltersList.value = { [option.id]: option };
  }

  if (!props.hasApply) {
    emits('set-options-activity', {
      filters: selectedFiltersList.value,
      category: props.value
    });
  }
}

function clearFiltersList() {
  selectedFiltersList.value = {};
  emits('set-options-activity', {
    filters: [],
    category: props.value,
  })
}

function applySelectedFilters() {
  emits('set-options-activity', {
    filters: Object.values(selectedFiltersList.value),
    category: props.value
  });
}

function initSort(onSetSortingType: TSortingKeys) {
  for (const sortingType in sorting.value) {
    if (sortingType != onSetSortingType) {
      sorting.value[sortingType as TSortingKeys] = null;
    }
  }

  sorting.value[onSetSortingType] = !sorting.value[onSetSortingType];
}

function closeAllFilterModal() {
  applySelectedFilters()
  emits('close-all-filter-modal')
}

initSort('price')
</script>

<template>
  <div
    class="filter-list"
    :class="isModalList && 'filter-list_modal'"
  >
    <div
      v-if="hasSearch && !isModalList"
      class="search-filter__search"
    >
      <LoupeIconSmall
        class="search-filter__search-loupe"
      />
      <input
        v-model="searchStringValue"
        type="text"
        :placeholder="searchPlaceholder"
      >
      <div class="search-filter__search-clear">
        <transition name="fade">
          <SearchClear
            v-if="searchStringValue.length"
            @click="searchStringValue = ''"
          />
        </transition>
      </div>
    </div>
    <div
      class="search-filter__body"
      :class="[
        !hasSearch && 'withoutSearch',
        !hasApply && 'withoutApply'
      ]"
    >
      <div class="search-filter__body-container">
        <div
          v-if="hasSearch && isModalList"
          class="search-filter__search"
        >
          <LoupeIconBig
            class="search-filter__search-loupe"
          />
          <input
            v-model="searchStringValue"
            type="text"
            :placeholder="searchPlaceholder"
          >
          <div class="search-filter__search-clear">
            <transition name="fade">
              <SearchClear
                v-if="searchStringValue.length"
                @click="searchStringValue = ''"
              />
            </transition>
          </div>
        </div>
        <div
          class="search-filter__sort"
          v-if="hasSort"
        >
          <div
            class="search-filter__sort-btn svg-fill-black"
            :class="[
              sorting.alphabet != null && 'active svg-black',
              !sorting.alphabet && 'rotate_arrow',
            ]"
            @click="initSort('alphabet')"
          >
            по алфавиту
            <ArrowSort/>
          </div>
          <div
            class="search-filter__sort-btn svg-fill-black"
            :class="[
              sorting.price != null && 'active svg-black',
              !sorting.price && 'rotate_arrow'
            ]"
            @click="initSort('price')"
          >
            по цене
            <ArrowSort/>
          </div>
        </div>
        <div
          class="search-filter__filters"
          :class="!hasSort && 'withoutSort'"
        >
          <template
            v-if="getOptionsList.length"
          >
            <div
              v-for="(option, index) in getOptionsList"
              :key="`${option.id}_${index}`"
              class="search-filter__filters-item"
            >
              <div
                class="search-filter__filters-item-container"
              >
                <AppCheckbox
                  v-if="!isRadio"
                  :checkbox-id="`${option.name}_${index}`"
                  :checkbox-label="String(option.name)"
                  :priority-checkbox-value="option.id in selectedFiltersList"
                  @update:model-value="selectFilter(option)"
                  checkbox-label-distance="small-distance"
                  :checkbox-size="isModalList ? 'l' : undefined"
                  class="search-filter__filters-item-checkbox"
                />
                <template v-else>
                  <AppRadio
                    :radio-id="option.id"
                    :radio-group="value || name"
                    :selected-radio-id="selectedFiltersList[option.id]?.id"
                    :label="String(option.name)"
                    @set-radio="selectFilter(option)"
                  />
                </template>
              </div>
              <div
                v-if="option.subName"
                class="search-filter__filters-item-container"
              >
                <span>{{ option.subName }}</span>
              </div>
            </div>
          </template>
          <template
            v-else
          >
            <div class="search-filter__filters-empty">
              Ничего не найдено
            </div>
          </template>
        </div>
      </div>
    </div>
    <div
      class="search-filter__buttons"
      v-if="hasApply"
    >
      <AppButton
        class="search-filter__buttons-btn"
        v-if="hasApply"
        @click="closeAllFilterModal"
        text="Выбрать"
        :size="isModalList ? 'm' : 'xs'"
      />
      <AppButton
        class="search-filter__buttons-btn"
        v-if="selectedOptionsCount && !isModalList"
        @click="clearFiltersList"
        text="Сбросить"
        type="neutral"
        size="xs"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@-moz-document url-prefix() {
  .search-filter__body-container {
    scrollbar-color: $dark-gray $white;
    scrollbar-width: thin;
  }
}

.search-filter {
  position: relative;

  &__search {
    position: relative;
    height: fit-content;
    padding: 0 12px;

    &-loupe {
      position: absolute;
      top: 10px;
      left: 24px;
    }

    input {
      width: 100%;
      height: 36px;
      @include font(14px, 20px);
      padding: 8px 28px 8px 36px;
      background-color: $bg_light-grey;
      border-radius: 6px;

      &::placeholder {
        color: $control-content_secondary;
        @include font(14px, 20px);
      }
    }

    &-clear {
      position: absolute;
      top: 50%;
      right: 16px;
      height: 16px;
      cursor: pointer;
      transform: translateY(-50%);

      @include mf-breakpoint {
        &:hover:not(:active) {
          svg {
            :deep(path) {
              fill: $content_primary;
            }
          }
        }
      }
    }
  }

  &__buttons {
    display: flex;
    gap: 8px;
    height: 60px;
    padding: 12px;

    @include md-breakpoint {
      margin-top: auto;
    }

    &-btn {
      flex: 1;
    }
  }

  &__body {
    padding: 0 4px 0 12px;
    margin-top: 16px;

    &-container {
      max-height: 332px;
      padding: 0 12px 0 0;
      overflow: auto;

      @include md-breakpoint {
        max-height: calc(100vh - 192px);
      }

      &::-webkit-scrollbar {
        width: 4px;
        background-color: $bg_page;
      }

      &::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background-color: $content_secondary;
      }
    }

    &.withoutApply {
      padding-bottom: 16px;
    }

    &.withoutSearch {
      margin-top: 0;

      .search-filter__body-container {
        max-height: 384px;
      }

      @include md-breakpoint {
        .search-filter__body-container {
          max-height: calc(100vh - 136px);
        }
      }
    }
  }

  &__sort {
    display: flex;
    justify-content: space-between;

    &-btn {
      color: $content_secondary;
      @include font(14px, 20px, 400);
      display: flex;
      align-items: center;
      cursor: pointer;
      transition: color .3s;

      &.rotate_arrow {
        svg {
          transform: rotateX(180deg);
        }
      }

      &:hover:not(:active),
      &.active {
        color: $content_primary;

        svg {
          transition: transform .2s;

          path {
            fill: $content_primary;
          }
        }
      }

      &.rotate {
        svg {
          transform: rotate(-180deg);
        }
      }
    }
  }

  &__filters {
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    &-empty {
      @include font(16px, 20px);
      text-align: center;
      color: $control-content_tretiary;
      margin-top: -2px;
    }

    &-item {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &-container {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      &-checkbox {
        @include md-breakpoint {
          :deep(.checkbox-text) {
            @include font(16px, 20px);
            text-transform: capitalize;
          }
        } 
      }

      span {
        @include font(14px, 20px);
        color: $content_secondary;
      }
    }

    &.withoutSort {
      padding-top: 0;
    }
  }
}

.filter-list {
  height: calc(100% - 64px);
  display: flex;
  flex-direction: column;
  z-index: 10;

  &_modal {
    .search-filter {
      &__search {
        padding: 0;
        margin-bottom: 24px;

        input {
          height: 40px;
          padding: 8px 12px 8px 44px;
          @include font(16px, 20px);

          &::placeholder {
            @include font(16px, 20px);
          }
        }

        &-loupe {
          top: 8px;
          left: 12px;
        }
      }

      &__body {
        padding: 0 4px 0 20px;
        margin-top: 0;

        &-container {
          max-height: calc(100vh - 24px);
          padding-bottom: 20px;

          @include md-breakpoint {
            max-height: calc(100vh - 192px);
          }
        }
      }

      &__buttons {
        padding: 0 20px 12px;
        position: relative;


        &:before {
          content: '';
          position: absolute;
          top: -20px;
          left: 0;
          width: calc(100% - 20px);
          height: 20px;
          background: linear-gradient(180deg, $control-bg_white 0%, rgba(255, 255, 255, 0) 0.01%, $control-bg_white 100%);
        }
      }

      &__filters {
        padding-top: 1px;
        gap: 24px;
      }
    }
  }
}
</style>
